<template>
  <div id="online-faqs">
    <h3>Online Workshops: Why Summit?</h3>
    <div>
      <p>
        As a national leader in providing high quality live workshops for over a decade, Summit understands that your time and money are extremely valuable -- that you don't want to settle for Online
        Continuing Education that is poor quality, poorly delivered, or won't be accepted by state and national licensing boards.
      </p>
      <p>
        With that as our foundation, we are proud to offer Online Continuing Education courses that we believe set a new standard of high quality. Leveraging our exceptional teaching faculty that
        delivers thousands of live workshops each year (with an <strong>average customer satisfaction rating of 4.7 out of 5.0</strong>), we have now invested in creating the same
        exceptional Online course experience.
      </p>
      <p>We deliver Online courses that are both high quality and easy to use:</p>
      <p><strong>We invest in high quality video production</strong></p>
      <ul>
        <li>Excellent sound, multiple cameras and camera angles</li>
        <li>"You are there" tight coverage of lab exercises</li>
        <li>Edited video content to focus on highest learning content</li>
      </ul>

      <p><strong>Our Online platform is state of the art</strong></p>
      <ul>
        <li>You can see video of the instructor and slides at the same time, plus also follow the course outline and download manual</li>
        <li>You can stop, start, & pick up again at your convenience (up to 30 days)</li>
        <li>We are mobile friendly, and also provide live telephone technical support, Monday - Friday 7AM to 6PM CST</li>
      </ul>
      <p>
        Finally, we are so confident that you will like our Online courses, we provide a <strong>100% satisfaction guarantee</strong>. Watch up to the first 30 minutes, and if you are not satisfied,
        you will receive a full refund. <em>Must contact Summit within initial 30-day viewing period.</em>
      </p>
    </div>

    <h3>On-Demand: What to Expect</h3>
    <div>
      <p>We want to make sure that you are 100% comfortable with what you are purchasing, so this is a rundown of the experience you should expect.</p>
      <p>Here's what you can expect from our media player:</p>
      <ul>
        <li>Watch high quality video of your instructor while also viewing the power point presentation</li>
        <li>Compatibility with both Mac and PC</li>
        <li>Mobile compatibility so you can watch on your phone or tablet</li>
      </ul>

      <p>We have also built in multiple helpful links within the media player so you can:</p>
      <ul>
        <li>Download the manual</li>
        <li>Get technical support</li>
        <li>FAQs to common questions</li>
        <li>Launch your post test and obtain your certification</li>
      </ul>

      <h4>Media Player Details</h4>
      <p>This is what the media player will look like when you first launch the video:</p>
      <img
        src="/img/playerimage.png"
        alt="Video Player Sample"
      />
      <p>
        <em>
          Note: The video will automatically start playing once you open the player. It may be beneficial to pause the video at the beginning so you can get organized and optimize your
          experience.
        </em>
      </p>
      <p><strong>Icons at the bottom</strong></p>
      <p>You will notice 4 Icons on the bottom of the player, this is what these tabs do:</p>
      <ul>
        <li>Helpful details regarding the course, anything you need before starting, and how the video player works</li>
        <li>This is where you can see the course outline and download your manual</li>
        <li>FAQs on the most common questions to enhance your video experience</li>
        <li>Where your post course test and certification will be located after you finish the video</li>
      </ul>

      <p><strong>Links in the top right hand corner</strong></p>
      <p>There are also 3 links in the upper right hand corner:</p>
      <ul>
        <li>Support - Gives you the number to the technical support team for issues with the media player</li>
        <li>Account - Takes you to your online profile, and is also where you log out of the media player</li>
        <li>Logout - Takes you back to the Summit Professional Education website</li>
      </ul>
    </div> <!-- section #1 -->

    <h3>Continuing Education Credit FAQs</h3>
    <h5>How do I know if the workshop offers CE credit for my profession and state?</h5>
    <div>
      <p>
        You can view the accreditation availability for each profession and state on the Product page of any course in the CE Credit tab (middle of page, far right tab).
      </p>
      <p>
        If you have further questions regarding accreditation, please contact Summit Customer Service M-F from 7 a.m. to 6 p.m. CST at (800) 433-9570 or email <a
          href="mailto:customerservice@summit-education.com"
          target="_blank"
        >customerservice@summit-education.com.</a>
      </p>
    </div>
    <h5>Why don’t all online workshops offer accreditation for my profession and state?</h5>
    <div>
      <p>The accreditation of online workshops varies by state and profession, and some licensing boards have longer review timelines or differing accreditation criteria than others.</p>
    </div>
    <h5>How many credits do I receive for an online course?</h5>
    <div>
      <p>Currently, all of our online workshops range from 1-6 hours in length. You can view the accreditation availability for each profession and state on the Product page (middle of page, far right tab of "CE Credit") where you can see the exact number of credit hours that may be offered for each profession and state.</p>
      <p>
        If you have further questions regarding CE credits, please contact Summit Customer Service M-F from 7 a.m. to 6 p.m. CST at (800) 433-9570 or email <a
          href="mailto:customerservice@summit-education.com"
          target="_blank"
        >customerservice@summit-education.com.</a>
      </p>
    </div>
    <h5>Does Summit report to ASHA?</h5>
    <div>
      <p>
        Yes! If you would like to obtain ASHA CEUs through Summit for completing an ASHA Registered course, it is necessary for you to submit a brief participant form to Summit immediately upon completion of your course.
      </p>
      <p>
        <strong>For online courses:</strong> Once you have met satisfactory completion requirements of the course, you will have access to the form via a <strong>"Report to ASHA" button</strong> in your account <u>(next to the "Certificate Download" button).</u> Simply select the <strong>"Report to ASHA" button, provide your ASHA member ID, and select "Submit".</strong> Please note, the <strong>"Report to ASHA" button</strong> <u>will only be available in your account to submit your request for <strong>10 days</strong> from the date you completed the online course</u> after which Summit will be unable to report your completion to ASHA.
      </p>
      <p><strong>Online course completions are reported to ASHA following the below schedule:</strong></p>
      <ul>
        <li>Completions dated 1/1 - 3/31 will be reported to ASHA no later than 5/15</li>
        <li>Completions dated 4/1 - 6/30 will be reported to ASHA no later than 8/14</li>
        <li>Completions dated 7/1 - 9/30 will be reported to ASHA no later than 11/14</li>
        <li>Completions dated 10/1 - 12/31 will be reported to ASHA no later than 2/14</li>
      </ul>
      <p>
        <strong>IMPORTANT:</strong> Please be sure the name and address listed on your Summit account matches what is associated with your ASHA Member record to ensure proper allocation of CEUs.
      </p>
      <p><strong>Does Summit report to CE broker?</strong></p>
      <p>
        Yes! Summit offers CE Broker reporting on behalf of <strong>eligible licensees in a selection of states.</strong> Information on which licensees are eligible and how to request CE Broker reporting can be found <a
          href="https://support.summit-education.com/hc/en-us/articles/360023278011-Does-Summit-report-to-CE-Broker-"
          target="_blank"
        >here.</a>
      </p>
    </div>

    <h3>Online Course Content FAQs</h3>
    <div>
      <p><strong>Is an outline of the course content available before or during viewing?</strong></p>
      <p>You can see a course outline on the Product page under the Course Outline tab. After purchase, the course outline is also provided on the media player for watching the course.</p>

      <p><strong>How do I access the course manual?</strong></p>
      <p>Once you have purchased the course, there is a tab at the bottom of the media player where you can download the manual to save or print for your personal use.</p>
      <p>Manual is located in the "Resources" tab</p>

      <p><strong>Are there labs in the course? What do I do if there are labs during the course?</strong></p>
      <p>
        Most of our courses have hands-on lab content, varying from minimal to significant. By looking at the Course Outline tab on the Product page, you can see how intensive the labs are for each
        course. During the lab section, we have invested heavily in providing very strong video content (i.e. very tight camera angles) so you can learn along with the live attendees. Depending on the
        course, you may want to simulate the activities that are presented in the course (which will be noted in the "Before You Start" section of the media player).
      </p>

      <p><strong>Where can I find who is teaching the course and his/her professional credentials?</strong></p>
      <p>You can see the instructor and their credentials on the product page under the Instructor Info tab</p>
    </div> <!-- online content faqs -->

    <h3>Online Video Experience</h3>
    <div>
      <p><strong>How long is the course?</strong></p>
      <p>Courses range in length from 1-6 hours. You can see the exact length of the course by visiting the course Product page.</p>
      <p><strong>Will I need any special materials to take the course?</strong></p>
      <p>After purchasing, the any special materials or specific advice is listed at the bottom of the "Course Info" tab.</p>
      <p><strong>Can I fast-forward or rewind through the online course?</strong></p>
      <p>
        Yes. For your convenience, you may fast-forward or rewind while watching the course. However, please note you will be required to certify you have watched the entire course to obtain CE
        credit.
      </p>
      <p><strong>Are there breaks? Can I press pause while watching the course?</strong></p>
      <p>There are no formal breaks in the video content. However, there is a pause feature to allow taking breaks at your own convenience. </p>
      <p><strong>Can I complete the course over several days? How much time do I have to finish the course?</strong></p>
      <p>
        Yes, you can always press pause or stop to take a break at your own convenience. Also, if you leave the course, please note where you left so you can pick up where you last finished. However,
        please remember you need to finish the course and pass your post course test within 30 days (non-subscribers) or before your subscription ends (subscribers).
      </p>
      <p><strong>Is the online course interactive? Will I be able to ask questions during the course?</strong></p>
      <p>
        The video that you will be watching is a recorded version of a live workshop. The video captures Instructor answers to participant questions, but there is no opportunity for you to ask
        questions with the instructor, real time. If you do have questions for the instructor, most instructors have their email address at the end of their presentations.
      </p>

      <p><strong>Am I able to re-watch the online courses even after I have completed the post course test and received my certificate?</strong></p>
      <p>
        Once you purchase the online workshop, you have 30 days to view the workshop as many times as you want as well as to review the manual (if you choose not to download while taking the course).
        For subscribers, your access is unlimited while your subscription is active.
      </p>
      <p><strong>Can I get a refund if I don't like the course?</strong></p>
      <p>
        We want your experience with Summit to be an enjoyable one. If you purchase an online workshop and don't like it for whatever reason, please stop within the first 30 minutes to receive a full
        refund. Then call us at (800) 433-9570 to talk with our customer service team to get your refund or a new online course.
      </p>
    </div><!-- online video exp faqs -->

    <h3>Test & Certificate</h3>
    <div>
      <p><strong>Will I need to take a post course test? Why?</strong></p>
      <p>Yes, you are required to successfully pass the post course test to obtain CE credits and certification. This is required by our licensing boards for online courses.</p>
      <p><strong>What is the post course test like, what is a passing score, and how many times can I take it?</strong></p>
      <p>
        Post course tests consist of True/False and/or multiple choice questions. You will be able to retake the post course test as many time as necessary to receive a passing grade. A passing
        "grade" is 75% or higher for general content, 80% or higher for certain state specific workshops, like ethics, jurisprudence, and laws and rules courses. You will be able to retake the post
        course test as many times as necessary to receive a passing grade.
      </p>
      <p><strong>When will I receive my credits and certificate?</strong></p>
      <p>Once you complete your course evaluation and online test, you will be able to immediately download your certificate and request CE Broker or ASHA Reporting (if applicable).</p>
    </div> <!-- test/cert -->

    <h3>Technical FAQs</h3>
    <div>
      <p><strong>What browsers and Operating system do I need to run for this to work properly?</strong></p>
      <p>The following are the supported browsers and Operating Systems for the media player to work properly:</p>
      <ul>
        <li>Internet Explorer 11 or Microsoft Edge 44+ for Windows 10</li>
        <li>Google Chrome 75.0+ for Windows, Mac OSX, or Linux</li>
        <li>Mozilla Firefox 70.0+ for Windows, Mac OSX, or Linux</li>
        <li>Safari 12.0+ for Mac OSX 10.13 and above</li>
      </ul>
      <p><strong>Will the media player work on both a Mac and PC?</strong></p>
      <p>Yes, the online courses will play on both a Mac and PC. The following are the supported browsers and Operating Systems for the media player to work properly:</p>
      <ul>
        <li>Internet Explorer 11 or Microsoft Edge 44+ for Windows 10</li>
        <li>Google Chrome 75.0+ for Windows, Mac OSX, or Linux</li>
        <li>Mozilla Firefox 70.0+ for Windows, Mac OSX, or Linux</li>
        <li>Safari 12.0+ for Mac OSX 10.13 and above</li>
      </ul>
      <p><strong>Can the workshops be played on my phone?</strong></p>
      <p>Yes! You are able to watch the workshops on your phone. Here are the technical requirements to ensure it works properly:</p>
      <ul>
        <li>Safari on iOS 13+</li>
        <li>Safari on iPadOS 13+</li>
        <li>Chrome on Android 9.0+</li>
        <li>Any running Android 9.0+</li>
      </ul>
      <p><strong>Are the videos downloadable or streaming?</strong></p>
      <p>All of the videos stream from the media player.</p>

      <p><strong>How does the streaming video work?</strong></p>
      <p>
        Live streaming video is content sent in compressed form over the Internet and displayed by the viewer in real time. With streaming video or streaming media, a Web user does not have to wait to
        download a file to play it. Instead, the media is sent in a continuous stream of data and is played as it arrives.
      </p>

      <p><strong>Will I experience any occasional buffering or freezing of the video?</strong></p>
      <p>
        Because of our streaming video approach, this rarely happens. If it does, it's typically because of customer internet access quality issues and we will provide troubleshooting tips as well as
        telephone support from our help desk.
      </p>

      <p><strong>Do you provide technical support?</strong></p>
      <p>Our help desk is available to assist you by phone Monday through Friday, 7AM to 6PM at (800) 433-9570 Central.</p>
    </div> <!-- technical faqs -->
  </div>
</template>
